import toast from "react-hot-toast";
import { DocumentUploadModalResponse, DocumentUploadModalForm } from "../types";
import { useUploadDocumentMutation } from "../../../../services/clientdocuments";

export type UseUploadDocumentHookProps = {
    isLoading: boolean,
    uploadDocument: (form: DocumentUploadModalForm) => Promise<DocumentUploadModalResponse>
};

export const useUploadDocument = () : UseUploadDocumentHookProps => {
    const [mutate, { isLoading }] = useUploadDocumentMutation();

    const buildForm = (form: DocumentUploadModalForm) : FormData => {
        const newFormData = new FormData();
        const { 
            conflictBehaviour, 
            documentDescription, 
            documentFunctionId, 
            documentType, 
            fileName, 
            fileSize, 
            isClientVisible,
            isClientSignaturesRequired,
            requiredSignatures,
            masterAccountId, 
            projectId,
            fileData
        } = form;
        
        // appends the file data that is being uploaded
        newFormData.append('attachment', fileData!, fileData!.name);

        // checks if we need to set the conflic behaviour
        // basically how graph is going to handle multiple files with the same name in the same folder
        if (conflictBehaviour && conflictBehaviour !== null) {
            newFormData.append('conflictBehaviour', conflictBehaviour);
        }

        // take care of the basic document/file data we set on the db record
        newFormData.append('documentFunctionId', documentFunctionId!);
        newFormData.append('documentType', documentType!.toString());
        newFormData.append('fileName', fileName);
        newFormData.append('fileSize', fileSize!.toString());
        
        // handle updating the description of the document
        if (documentDescription && documentDescription.length > 0) {
            newFormData.append('documentDescription', documentDescription);
        }

        if (masterAccountId && masterAccountId !== null) {
            newFormData.append('masterAccountId', masterAccountId!);
            newFormData.append('isClientVisible', isClientVisible === true ? 'true' : 'false');

            // NOTE: only set these properties because the master account id has been set
            // handle the project set
            if (projectId && projectId !== null) {
                newFormData.append('projectId', projectId);
            }

            // handle the document signatures
            if (isClientSignaturesRequired && requiredSignatures.length > 0) {
                requiredSignatures.forEach((el, i) => {
                    newFormData.append(`requiredSignatures[${i}].clientAccountId`, el.clientAccountId);
                    
                    // only add the authorisation code IF we have one set
                    if (el.authorisationCode && el.authorisationCode.length > 0) {
                        newFormData.append(`requiredSignatures[${i}].authorisationCode`, el.authorisationCode);
                    }
                });
            }
        }
        else {
            newFormData.append('isClientVisible', 'false');
        }

        return newFormData;
    };

    const onUploadHandler = (form: DocumentUploadModalForm) : Promise<DocumentUploadModalResponse> => new Promise<DocumentUploadModalResponse>((resolve) => {
        const request = buildForm(form);
        return toast.promise<DocumentUploadModalResponse[]>(mutate(request).unwrap(), {
            loading: 'Processing document upload.',
            success: 'Your document has been uploaded.',
            error: 'There was a problem with your document upload. Unable to complete your request.'
        }).then(
            (r: DocumentUploadModalResponse[]) => resolve(r[0]),
            () => resolve({ status: 400, message: 'Failed to upload the document', documentId: null } as DocumentUploadModalResponse)
        )
    });

    return {
        isLoading,
        uploadDocument: onUploadHandler
    }
};